import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Divider,
  Stack,
  Grid,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  AttachMoney as PriceIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import PreciosModal from './PreciosModal';
import { useCatalogoExcel } from './catalogoExcel';

const Catalogo = () => {
  const [catalogos, setCatalogos] = useState([]);
  const [originalCatalogos, setOriginalCatalogos] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCatalogo, setSelectedCatalogo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    id_pet: '',
    id_sub_categoria: '',
    id_usuario: '',
    id_marca: '',
    referencia: '',
    descripcion: '',
    codigo_barras: '',
    costo: 0,
    unidades_fraccion: 0,
    descripcion_fraccion: '',
    habilitado: true,
    es_servicio: false,
    es_descontinuado: false,
    es_exento: false,
    es_perm_descuento: false,
  });
  const [subCategorias, setSubCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdUsuario, setUserIdUsuario] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [referenciaLoading, setReferenciaLoading] = useState(false);
  const navigate = useNavigate();

  // ===== PRECIOS =====
  const [preciosMap, setPreciosMap] = useState({});
  const [listasPrecio, setListasPrecio] = useState([]);
  const [preciosModalOpen, setPreciosModalOpen] = useState(false);
  const [preciosModalCatalogo, setPreciosModalCatalogo] = useState(null);

  // Snackbar helper compartido
  const notify = (mensaje) => {
    setSnackbarMessage(mensaje);
    setSnackbarOpen(true);
  };
  const notifyError = (mensaje) => {
    setError(mensaje);
    notify(mensaje);
  };

  const openPreciosModalFor = (catalogo) => {
    setPreciosModalCatalogo(catalogo);
    setPreciosModalOpen(true);
  };
  const closePreciosModal = () => {
    setPreciosModalOpen(false);
    setPreciosModalCatalogo(null);
  };

  // ===== EXCEL =====
  const { importing, handleExportExcel, handleImportExcel } = useCatalogoExcel({
    userIdPet,
    userIdUsuario,
    onNotify: notify,
    onError: notifyError,
  });

  // Decodificar token JWT para obtener id_pet y id_usuario
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdPet(payload.id_pet || null);
        setUserIdUsuario(payload.id_usuario || null);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        notifyError('Error al decodificar el token de usuario');
      }
    } else {
      notifyError('No se encontró un token de usuario');
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Formatear moneda
  const formatMoney = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '—';
    return Number(value).toLocaleString('es-CR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  };

  // Normalizar datos de catálogo
  const normalizeCatalogo = (catalogo) => {
    if (!catalogo || !catalogo.id_catalogo) {
      console.warn('Catálogo inválido recibido:', catalogo);
      return null;
    }
    return {
      id_catalogo: catalogo.id_catalogo ?? null,
      id_pet: catalogo.id_pet ?? null,
      id_sub_categoria: catalogo.id_sub_categoria ?? null,
      id_usuario: catalogo.id_usuario ?? null,
      id_marca: catalogo.id_marca ?? null,
      referencia: catalogo.referencia ?? '',
      descripcion: catalogo.descripcion ?? '',
      codigo_barras: catalogo.codigo_barras ?? '',
      costo: catalogo.costo ?? 0,
      unidades_fraccion: catalogo.unidades_fraccion ?? 0,
      descripcion_fraccion: catalogo.descripcion_fraccion ?? '',
      habilitado: catalogo.habilitado ?? true,
      es_servicio: catalogo.es_servicio ?? false,
      es_descontinuado: catalogo.es_descontinuado ?? false,
      es_exento: catalogo.es_exento ?? false,
      es_perm_descuento: catalogo.es_perm_descuento ?? false,
      createdAt: catalogo.createdAt ?? null,
      updatedAt: catalogo.updatedAt ?? null,
      tab_categoria: catalogo.tab_categoria ?? '',
    };
  };

  // Obtener catálogos, subcategorías, marcas, pets y listas de precio
  useEffect(() => {
    if (!userIdPet || !userIdUsuario) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const catalogosRes = await api.get(`/catalogos/con-precios`);
        console.log('Respuesta de catálogos:', catalogosRes.data);
        const catalogosData = Array.isArray(catalogosRes.data)
          ? catalogosRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const normalizedCatalogos = catalogosData
          .map(normalizeCatalogo)
          .filter(cat => cat !== null && cat.id_catalogo && cat.id_pet === userIdPet);
        console.log('Catálogos normalizados:', normalizedCatalogos);
        setCatalogos(normalizedCatalogos);
        setOriginalCatalogos(normalizedCatalogos);

        const preciosResumenMap = {};
        catalogosData.forEach((item) => {
          if (item && item.id_catalogo) {
            preciosResumenMap[item.id_catalogo] = Array.isArray(item.precios) ? item.precios : [];
          }
        });
        setPreciosMap(preciosResumenMap);

        const subCategoriasRes = await api.get(`/sub-categorias/`);
        console.log('Respuesta de subcategorías:', subCategoriasRes.data);
        const subCategoriasData = Array.isArray(subCategoriasRes.data)
          ? subCategoriasRes.data.filter(item => item !== null && item !== undefined)
          : [];
        setSubCategorias(subCategoriasData);

        const marcasRes = await api.get('/marcas/');
        console.log('Respuesta de marcas:', marcasRes.data);
        const marcasData = Array.isArray(marcasRes.data)
          ? marcasRes.data.filter(item => item !== null && item !== undefined)
          : [];
        setMarcas(marcasData);

        const petsRes = await api.get('/pets/');
        console.log('Respuesta de pets:', petsRes.data);
        const petsData = Array.isArray(petsRes.data) ? petsRes.data : [];
        setPets(petsData);

        try {
          const listasRes = await api.get('/listas-precio/', {
            params: { solo_activas: true },
          });
          const listasData = Array.isArray(listasRes.data) ? listasRes.data : [];
          setListasPrecio(listasData);
        } catch (listasErr) {
          console.warn('No se pudieron cargar las listas de precio:', listasErr);
          setListasPrecio([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        notifyError('Error al cargar datos: ' + (error.response?.data?.detail || error.message));
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet, userIdUsuario]);

  // ==================== LÓGICA AUTOCOMPLETE SUBCATEGORÍA ====================
  const selectedSubCategoria = useMemo(() =>
    subCategorias.find(sc => sc.id_sub_categoria === formData.id_sub_categoria) || null,
    [formData.id_sub_categoria, subCategorias]
  );

  const filteredSubCategorias = useMemo(() => {
    const opciones = subCategorias.filter(sc => sc.id_pet === userIdPet && sc.habilitada === true);
    if (selectedCatalogo && selectedSubCategoria && selectedSubCategoria.habilitada === false) {
      if (!opciones.some(sc => sc.id_sub_categoria === selectedSubCategoria.id_sub_categoria)) {
        opciones.push(selectedSubCategoria);
      }
    }
    return opciones;
  }, [subCategorias, userIdPet, selectedCatalogo, selectedSubCategoria]);

  // ==================== LÓGICA AUTOCOMPLETE MARCA ====================
  const selectedMarca = useMemo(() =>
    marcas.find(m => m.id_marca === formData.id_marca) || null,
    [formData.id_marca, marcas]
  );

  const filteredMarcas = useMemo(() => {
    const opciones = marcas.filter(m => m.id_pet === formData.id_pet && m.habilitada === true);
    if (selectedCatalogo && selectedMarca && selectedMarca.habilitada === false) {
      if (!opciones.some(m => m.id_marca === selectedMarca.id_marca)) {
        opciones.push(selectedMarca);
      }
    }
    return opciones;
  }, [marcas, formData.id_pet, selectedCatalogo, selectedMarca]);

  // Buscar catálogos en frontend
  useEffect(() => {
    const searchCatalogos = () => {
      if (!searchTerm) {
        setCatalogos(originalCatalogos);
        return;
      }
      const filteredCatalogos = originalCatalogos
        .filter(cat => cat && cat.id_catalogo && (cat.referencia || cat.descripcion))
        .filter(cat =>
          (cat.referencia && cat.referencia.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (cat.descripcion && cat.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      setCatalogos(filteredCatalogos);
      setPage(0);
    };
    searchCatalogos();
  }, [searchTerm, originalCatalogos]);

  // Abrir modal para crear/editar catálogo
  const handleOpenModal = async (catalogo = null) => {
    setSelectedCatalogo(catalogo);
    if (catalogo) {
      setFormData(normalizeCatalogo(catalogo));
    } else {
      setReferenciaLoading(true);
      setFormData({
        id_pet: userIdPet || '',
        id_sub_categoria: '',
        id_usuario: userIdUsuario || '',
        id_marca: '',
        referencia: '',
        descripcion: '',
        codigo_barras: '',
        costo: 0,
        unidades_fraccion: 0,
        descripcion_fraccion: '',
        habilitado: true,
        es_servicio: false,
        es_descontinuado: false,
        es_exento: false,
        es_perm_descuento: false,
      });
      try {
        const lastReferenciaRes = await api.get('/catalogos/last-referencia');
        setFormData(prevFormData => ({
          ...prevFormData,
          referencia: String(lastReferenciaRes.data.referencia),
        }));
      } catch (err) {
        console.error('Error al obtener la siguiente referencia:', err);
        setFormData(prevFormData => ({ ...prevFormData, referencia: '' }));
        notifyError('Error al obtener la siguiente referencia: ' + (err.response?.data?.detail || err.message));
      } finally {
        setReferenciaLoading(false);
      }
    }
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar catálogo (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.referencia || !formData.descripcion || !formData.id_pet || !formData.id_sub_categoria || !formData.id_usuario || !formData.id_marca) {
        notifyError('Referencia, descripción, veterinaria, subcategoría, usuario y marca son obligatorios');
        return;
      }
      if (formData.es_servicio || formData.es_descontinuado) {
        formData.costo = 0;
      }
      const payload = {
        id_pet: formData.id_pet,
        id_sub_categoria: formData.id_sub_categoria,
        id_usuario: formData.id_usuario,
        id_marca: formData.id_marca,
        referencia: formData.referencia,
        descripcion: formData.descripcion,
        codigo_barras: formData.codigo_barras || null,
        costo: formData.costo,
        unidades_fraccion: formData.unidades_fraccion,
        descripcion_fraccion: formData.descripcion_fraccion || null,
        habilitado: formData.habilitado,
        es_servicio: formData.es_servicio,
        es_descontinuado: formData.es_descontinuado,
        es_exento: formData.es_exento,
        es_perm_descuento: formData.es_perm_descuento,
      };
      if (selectedCatalogo) {
        const response = await api.put(`/catalogos/${selectedCatalogo.id_catalogo}`, payload);
        const updatedCatalogo = normalizeCatalogo(response.data);
        if (updatedCatalogo) {
          const newCatalogos = catalogos.map(cat =>
            cat.id_catalogo === selectedCatalogo.id_catalogo ? updatedCatalogo : cat
          );
          setCatalogos(newCatalogos);
          setOriginalCatalogos(newCatalogos);
          notify('Catálogo actualizado exitosamente');
        }
      } else {
        const response = await api.post('/catalogos/', payload);
        const newCatalogo = normalizeCatalogo(response.data);
        if (newCatalogo) {
          const newCatalogos = [...catalogos, newCatalogo];
          setCatalogos(newCatalogos);
          setOriginalCatalogos(newCatalogos);
          notify('Catálogo creado exitosamente. Ahora puedes agregarle precios.');
          setTimeout(() => {
            openPreciosModalFor(newCatalogo);
          }, 600);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar catálogo:', error);
      notifyError('Error al guardar catálogo: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Deshabilitar catálogo
  const handleToggleHabilitado = async (id, habilitado) => {
    try {
      const response = await api.put(`/catalogos/${id}`, { habilitado: !habilitado });
      const updatedCatalogo = normalizeCatalogo(response.data);
      if (updatedCatalogo) {
        const newCatalogos = catalogos.map(cat =>
          cat.id_catalogo === id ? updatedCatalogo : cat
        );
        setCatalogos(newCatalogos);
        setOriginalCatalogos(newCatalogos);
        notify(`Catálogo ${updatedCatalogo.habilitado ? 'habilitado' : 'deshabilitado'} exitosamente`);
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      notifyError('Error al actualizar estado: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Helper: obtener el precio más alto de un ítem
  const getPrecioAlto = (idCatalogo) => {
    const list = preciosMap[idCatalogo] || [];
    if (list.length === 0) return null;
    return Math.max(...list.map(p => Number(p.precio) || 0));
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedCatalogos = catalogos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Catálogo
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar en catálogo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet || !userIdUsuario ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : catalogos.length > 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<ExportIcon />}
                  onClick={handleExportExcel}
                >
                  Exportar Excel
                </Button>

                <Button
                  variant="outlined"
                  color="info"
                  component="label"
                  startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
                  disabled={importing}
                >
                  {importing ? 'Importando...' : 'Importar Excel'}
                  <input type="file" hidden accept=".xlsx, .xls" onChange={handleImportExcel} />
                </Button>

                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
                  Nuevo Ítem
                </Button>
              </Box>
              <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', boxShadow: 3 }}>
                <Table sx={{ tableLayout: 'auto' }} aria-label="tabla de catálogo">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Referencia</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>SubCategoría</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Marca</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Costo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Precio(s)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Habilitado</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Servicio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Descontinuado</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Creado</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Actualizado</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCatalogos.map((row) => {
                      if (!row || !row.id_catalogo) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      const preciosDelItem = preciosMap[row.id_catalogo] || [];
                      const precioAlto = getPrecioAlto(row.id_catalogo);

                      return (
                        <TableRow key={row.id_catalogo} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>{row.id_catalogo}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>{row.referencia}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, fontWeight: 'bold' }}>{row.descripcion}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {subCategorias.find(sc => sc.id_sub_categoria === row.id_sub_categoria)?.nombre_sub_categoria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {marcas.find(m => m.id_marca === row.id_marca)?.nombre_marca || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>{formatMoney(row.costo)}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {preciosDelItem.length === 0 ? (
                              <Chip
                                label="Sin precios"
                                size="small"
                                color="warning"
                                variant="outlined"
                                onClick={() => openPreciosModalFor(row)}
                                sx={{ cursor: 'pointer' }}
                              />
                            ) : (
                              <Tooltip
                                title={
                                  <Box>
                                    {preciosDelItem.map(p => (
                                      <Typography key={p.id} variant="body2">
                                        $ {formatMoney(p.precio)}
                                        {p.nombre_lista && p.nombre_lista !== 'Sin lista' ? ` (${p.nombre_lista})` : ''}
                                      </Typography>
                                    ))}
                                  </Box>
                                }
                              >
                                <Chip
                                  icon={<PriceIcon />}
                                  label={
                                    preciosDelItem.length === 1
                                      ? `$ ${formatMoney(precioAlto)}`
                                      : `$ ${formatMoney(precioAlto)} (${preciosDelItem.length})`
                                  }
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  onClick={() => openPreciosModalFor(row)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <Switch
                              checked={row.habilitado === true || row.habilitado === false ? row.habilitado : false}
                              onChange={() => handleToggleHabilitado(row.id_catalogo, row.habilitado ?? false)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>{row.es_servicio ? 'Sí' : 'No'}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>{row.es_descontinuado ? 'Sí' : 'No'}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>{formatDate(row.createdAt)}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>{formatDate(row.updatedAt)}</TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <Tooltip title="Gestionar precios">
                              <IconButton onClick={() => openPreciosModalFor(row)} color="success" size="small">
                                <PriceIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar ítem">
                              <IconButton onClick={() => handleOpenModal(row)} color="primary" size="small">
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button variant="contained" disabled={page === 0} onClick={() => handleChangePage(page - 1)}>
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(catalogos.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(catalogos.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron ítems en el catálogo. Crea un nuevo ítem para comenzar.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()} sx={{ mt: 2 }}>
              Nuevo Ítem
            </Button>
          </Box>
        )}

        {/* ===================== MODAL DE EDICIÓN DE ÍTEM ===================== */}
        <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>{selectedCatalogo ? 'Editar ítem' : 'Nuevo ítem'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* --- Identificación --- */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Identificación
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Referencia"
                      fullWidth
                      value={formData.referencia}
                      onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                      required
                      disabled={!selectedCatalogo || referenciaLoading}
                      InputProps={{
                        endAdornment: referenciaLoading && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Código de barras"
                      fullWidth
                      value={formData.codigo_barras}
                      onChange={(e) => setFormData({ ...formData, codigo_barras: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción"
                      fullWidth
                      multiline
                      minRows={2}
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      required
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* --- Clasificación --- */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Clasificación
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label="Veterinaria"
                      fullWidth
                      value={formData.id_pet}
                      onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
                      required
                      disabled
                    >
                      {pets.length > 0 ? (
                        pets.map((pet) => (
                          <MenuItem key={pet.id_pet} value={pet.id_pet}>
                            {pet.nombre_veterinaria}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>No hay veterinarias disponibles</MenuItem>
                      )}
                    </TextField>
                  </Grid>

                  {/* AUTOCOMPLETE DE SUBCATEGORÍA */}
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={filteredSubCategorias}
                      getOptionLabel={(option) => option ? option.nombre_sub_categoria || '' : ''}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id_sub_categoria}>
                          {option.nombre_sub_categoria}
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => option.id_sub_categoria === value.id_sub_categoria}
                      value={selectedSubCategoria}
                      onChange={(e, newValue) => setFormData({ ...formData, id_sub_categoria: newValue ? newValue.id_sub_categoria : '' })}
                      renderInput={(params) => <TextField {...params} label="Subcategoría *" fullWidth required />}
                    />
                  </Grid>

                  {/* AUTOCOMPLETE DE MARCA */}
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={filteredMarcas}
                      getOptionLabel={(option) => option ? option.nombre_marca || '' : ''}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id_marca}>
                          {option.nombre_marca}
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => option.id_marca === value.id_marca}
                      value={selectedMarca}
                      onChange={(e, newValue) => setFormData({ ...formData, id_marca: newValue ? newValue.id_marca : '' })}
                      disabled={!formData.id_pet}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Marca *"
                          fullWidth
                          required
                          placeholder={!formData.id_pet ? "Selecciona una veterinaria primero" : ""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* --- Configuración --- */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Configuración
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.habilitado}
                          onChange={(e) => setFormData({ ...formData, habilitado: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="Habilitado"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.es_servicio}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              es_servicio: e.target.checked,
                              costo: e.target.checked ? 0 : formData.costo,
                            })
                          }
                          color="primary"
                        />
                      }
                      label="Servicio"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.es_descontinuado}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              es_descontinuado: e.target.checked,
                              costo: e.target.checked ? 0 : formData.costo,
                            })
                          }
                          color="primary"
                        />
                      }
                      label="Descontinuado"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.referencia || !formData.descripcion || !formData.id_pet || !formData.id_sub_categoria || !formData.id_usuario || !formData.id_marca || referenciaLoading}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        {/* ===================== MODAL DE PRECIOS ===================== */}
        <PreciosModal
          open={preciosModalOpen}
          catalogo={preciosModalCatalogo}
          userIdPet={userIdPet}
          listasPrecio={listasPrecio}
          onClose={closePreciosModal}
          onNotify={notify}
          onPreciosChange={(idCatalogo, precios) =>
            setPreciosMap(prev => ({ ...prev, [idCatalogo]: precios }))
          }
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Catalogo;